import { differenceInYears } from "date-fns";
import {
  gfValidateEmailField,
  gfValidateNameTextField,
  gfValidatePasswordField
} from "../../../global/forms/globalFormValidation";

/** @deprecated Prefer {@link gfValidateNameTextField} from global form validation. */
export function isValidUserDisplayName(name) {
  if (!name || typeof name !== "string") return false;
  return gfValidateNameTextField(name) === "";
}

export function toDateInputValue(v) {
  if (v == null || v === "") return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return v.slice(0, 10);
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function getMaxDateOfBirthForStudent() {
  const today = new Date();
  const maxDateObj = new Date();
  maxDateObj.setFullYear(today.getFullYear() - 6);
  const y = maxDateObj.getFullYear();
  const m = String(maxDateObj.getMonth() + 1).padStart(2, "0");
  const d = String(maxDateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function validateStudentRegistration(
  formData,
  { passwordMode = "required" } = {},
) {
  const errors = {};
  const full_name = String(formData.full_name ?? "").trim();
  const email = String(formData.email ?? "").trim();
  const address = String(formData.address ?? "").trim();
  const guardian_name = String(formData.guardian_name ?? "").trim();
  const contact_number = String(formData.contact_number ?? "").replace(/\D/g, "");
  const emergency_contact = String(formData.emergency_contact ?? "").replace(
    /\D/g,
    "",
  );
  const gender = String(formData.gender ?? "").trim().toLowerCase();
  const date_of_birth = String(formData.date_of_birth ?? "").trim();

  if (!full_name) {
    errors.full_name = "Full name is required";
  } else {
    const fnErr = gfValidateNameTextField(full_name);
    if (fnErr) errors.full_name = fnErr;
  }
  if (!email) {
    errors.email = "Email is required";
  } else {
    const emailErr = gfValidateEmailField(email);
    if (emailErr) errors.email = emailErr;
  }
  if (!contact_number || !/^[0-9]{10}$/.test(contact_number)) {
    errors.contact_number = "Contact number must be exactly 10 digits";
  }
  if (!emergency_contact || !/^[0-9]{10}$/.test(emergency_contact)) {
    errors.emergency_contact =
      "Emergency contact number must be exactly 10 digits";
  }
  if (
    contact_number &&
    emergency_contact &&
    /^[0-9]{10}$/.test(contact_number) &&
    /^[0-9]{10}$/.test(emergency_contact) &&
    contact_number === emergency_contact
  ) {
    errors.emergency_contact =
      "Emergency contact number must be different from contact number";
  }
  if (!date_of_birth) {
    errors.date_of_birth = "Date of birth is required";
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(date_of_birth)) {
    errors.date_of_birth = "Invalid date of birth";
  } else {
    const dob = new Date(date_of_birth);
    if (Number.isNaN(dob.getTime())) {
      errors.date_of_birth = "Invalid date of birth";
    } else {
      const now = new Date();
      if (dob > now) {
        errors.date_of_birth = "Date of birth cannot be in the future";
      } else if (differenceInYears(now, dob) < 6) {
        errors.date_of_birth = "Age must be at least 6 years";
      }
    }
  }
  if (!gender || !["male", "female", "others"].includes(gender)) {
    errors.gender = "Gender is required";
  }
  if (!guardian_name) {
    errors.guardian_name = "Parent / guardian name is required";
  } else {
    const gnErr = gfValidateNameTextField(guardian_name);
    if (gnErr) errors.guardian_name = gnErr;
  }

  const password = String(formData.password ?? "");
  const password_confirm = String(formData.password_confirm ?? "");

  if (passwordMode === "required") {
    if (!password) {
      errors.password = "Password is required";
    } else {
      const pwErr = gfValidatePasswordField(password);
      if (pwErr) errors.password = pwErr;
      else if (password !== password_confirm) {
        errors.password_confirm = "Passwords do not match";
      }
    }
  } else if (passwordMode === "optional") {
    const hasP = password.trim().length > 0;
    const hasC = password_confirm.trim().length > 0;
    if (hasP !== hasC) {
      errors.password =
        "Enter both new password and confirmation, or leave both blank to keep your current password";
    } else if (hasP && hasC) {
      const pwErr = gfValidatePasswordField(password);
      if (pwErr) errors.password = pwErr;
      else if (password !== password_confirm) {
        errors.password_confirm = "Passwords do not match";
      }
    }
  }

  if (!address) {
    errors.address = "Address is required";
  } else if (address.length > 500) {
    errors.address = "Address cannot exceed 500 characters";
  }

  const sportId = Number(formData.sport_interested_id);
  if (
    formData.sport_interested_id === undefined ||
    formData.sport_interested_id === null ||
    formData.sport_interested_id === "" ||
    !Number.isFinite(sportId) ||
    sportId < 1
  ) {
    errors.sport_interested_id = "Please select a sport";
  }

  const payload = {
    full_name,
    date_of_birth,
    gender,
    contact_number,
    email,
    address,
    guardian_name,
    emergency_contact,
    sport_interested_id: sportId,
    photo: "/seed/student-profile/400/400",
  };
  if (passwordMode === "required") {
    payload.password = password;
    payload.password_confirm = password_confirm;
  } else if (passwordMode === "optional" && password.trim() && password_confirm.trim()) {
    payload.password = password;
    payload.password_confirm = password_confirm;
  }

  return {
    ok: Object.keys(errors).length === 0,
    errors,
    payload,
  };
}
